import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Card } from '@mui/material';
import DocumentView from '../components/documents/DocumentView';
import Seo from '../components/Seo';

/**
 * Public hosted page for a quote or invoice, reached via a signed token link
 * (/quote/:token or /invoice/:token). No authentication required.
 */
const DocumentPage = () => {
  const { token } = useParams();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState('');

  const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  const loadDocument = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/api/public/documents/${token}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to load this document.');
      setDocument(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBase, token]);

  useEffect(() => { loadDocument(); }, [loadDocument]);

  const handleAccept = async () => {
    setAccepting(true);
    setAcceptError('');
    try {
      const res = await fetch(`${apiBase}/api/public/documents/${token}/accept`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to accept this quote.');
      setDocument(data.document);
    } catch (err) {
      setAcceptError(err.message);
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', px: 2 }}>
        <Card sx={{ p: 4, maxWidth: 460, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>This document isn’t available</Typography>
          <Typography variant="body2" color="text.secondary">{error}</Typography>
        </Card>
      </Box>
    );
  }

  // After an online payment: invoices return the refreshed document; quote
  // payments return the created booking, so re-fetch to show updated status.
  const handlePaid = (result) => {
    if (result && result.type === 'INVOICE') {
      setDocument(result);
    } else {
      loadDocument();
    }
  };

  return (
    <>
      <Seo title="Your Document" noindex />
      <DocumentView
        document={document}
        onAccept={handleAccept}
        accepting={accepting}
        acceptError={acceptError}
        payToken={token}
        onPaid={handlePaid}
      />
    </>
  );
};

export default DocumentPage;
